import AttachmentIcon from "@atlaskit/icon/core/attachment";
import ErrorIcon from "@atlaskit/icon/core/error";
import {token} from "@atlaskit/tokens";
import {useRef, useState} from "react";
import {BtlActivityTemplateMarkdownSection} from "~/components/btlActivityTemplateComponents/markdownSection";
import {NoteAttachmentTile} from "~/components/notes/noteAttachmentTile";
import {NOTES_COMPOSER_BOX_STYLE} from "~/components/notes/notesPanelTypography";
import {
    computeNotePlainTextLength,
    NOTES_BODY_MAX_PLAIN_LENGTH,
    NOTES_MAX_ATTACHMENTS,
    type NoteAttachment,
} from "~/domainModels/notes";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";

const COMPOSER_PLACEHOLDER = "Start typing your text...";

export function NotesComposer({
    composerId,
    value,
    onChange,
    stagedAttachments,
    onStagedAttachmentsChange,
    onSave,
    onCancel,
    isSaving,
    isUploading,
    onUploadFiles,
    showCancel = false,
    saveLabel = "Save",
    showFooter = true,
}: {
    composerId: Uuid;
    value: string;
    onChange: (next: string) => void;
    stagedAttachments: Array<NoteAttachment>;
    onStagedAttachmentsChange: (next: Array<NoteAttachment>) => void;
    onSave: () => void;
    onCancel?: () => void;
    isSaving: boolean;
    isUploading: boolean;
    onUploadFiles: (files: FileList | null) => void;
    showCancel?: boolean;
    saveLabel?: string;
    showFooter?: boolean;
}) {
    const [markdownError, setMarkdownError] = useState<string | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [userActivated, setUserActivated] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const plainLength = computeNotePlainTextLength(value);
    const isOverLimit = plainLength > NOTES_BODY_MAX_PLAIN_LENGTH;
    const hasDraft = plainLength > 0 || stagedAttachments.length > 0;
    const isAtAttachmentLimit = stagedAttachments.length >= NOTES_MAX_ATTACHMENTS;
    const canSave =
        isSaving === false
        && isUploading === false
        && isOverLimit === false
        && hasDraft === true;

    const isWriting = showCancel === true || userActivated === true || hasDraft === true;

    const handleMarkdownChange = (next: string) => {
        onChange(next);
        const length = computeNotePlainTextLength(next);
        if (length > NOTES_BODY_MAX_PLAIN_LENGTH) {
            setMarkdownError(
                "Character limit reached. You have reached the maximum character limit for notes.",
            );
        } else {
            setMarkdownError(null);
        }
    };

    const handleFilesSelected = (files: FileList | null) => {
        if (files === null || files.length === 0) {
            return;
        }
        const remainingSlots = NOTES_MAX_ATTACHMENTS - stagedAttachments.length;
        if (remainingSlots <= 0) {
            setUploadError(
                `Maximum attachment limit reached. Only ${NOTES_MAX_ATTACHMENTS} files can be added.`,
            );
            return;
        }
        const selected = Array.from(files);
        if (selected.length > remainingSlots) {
            setUploadError(
                `Maximum attachment limit reached. Only ${NOTES_MAX_ATTACHMENTS} files can be added.`,
            );
            const capped = new DataTransfer();
            for (const file of selected.slice(0, remainingSlots)) {
                capped.items.add(file);
            }
            onUploadFiles(capped.files);
            return;
        }
        setUploadError(null);
        onUploadFiles(files);
    };

    const footer = (
        <div
            className="grid grid-cols-[minmax(0,1fr)_max-content] items-center gap-ds-space-100 border-t px-ds-space-150 py-ds-space-100"
            style={{borderColor: token("color.border")}}
        >
            <AnankeText size="small" color="color.text.subtlest">
                {plainLength}/{NOTES_BODY_MAX_PLAIN_LENGTH}
            </AnankeText>
            <div className="grid grid-flow-col auto-cols-max gap-ds-space-100">
                {showCancel === true && onCancel !== undefined ? (
                    <AnankeButton type="button" appearance="subtle" onClick={onCancel}>
                        Cancel
                    </AnankeButton>
                ) : null}
                <AnankeButton
                    type="button"
                    appearance="primary"
                    isDisabled={canSave === false}
                    isLoading={isSaving === true || isUploading === true}
                    onClick={onSave}
                >
                    {saveLabel}
                </AnankeButton>
            </div>
        </div>
    );

    if (isWriting === false) {
        return (
            <div className="grid overflow-hidden" style={NOTES_COMPOSER_BOX_STYLE}>
                <button
                    type="button"
                    className="grid min-h-[7.5rem] w-full cursor-text justify-items-start border-0 bg-transparent p-ds-space-150 text-left"
                    onClick={() => setUserActivated(true)}
                >
                    <span
                        className="text-ds-font-body"
                        style={{color: token("color.text.subtlest")}}
                    >
                        {COMPOSER_PLACEHOLDER}
                    </span>
                </button>
                {showFooter === true ? footer : null}
            </div>
        );
    }

    return (
        <div className="grid overflow-hidden" style={NOTES_COMPOSER_BOX_STYLE}>
            <div className="grid gap-0">
                <BtlActivityTemplateMarkdownSection
                    sectionId={composerId}
                    sectionTitle="Note"
                    value={value}
                    setValue={(next) => {
                        const resolved = typeof next === "function" ? next(value) : next;
                        handleMarkdownChange(resolved);
                    }}
                    error={markdownError}
                    setError={setMarkdownError}
                    mode="editable"
                    showInlineEditToggle={false}
                    useContainer={false}
                    editorLayout="inline"
                    toolbarLeadingContent={
                        <>
                            <AnankeIconButton
                                type="button"
                                appearance="subtle"
                                icon={AttachmentIcon}
                                label="Attach file"
                                isDisabled={isUploading === true || isAtAttachmentLimit === true}
                                onClick={() => fileInputRef.current?.click()}
                            />
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                className="hidden"
                                onChange={(event) => {
                                    handleFilesSelected(event.currentTarget.files);
                                    event.currentTarget.value = "";
                                }}
                            />
                        </>
                    }
                />
                {stagedAttachments.length > 0 ? (
                    <div className="grid grid-flow-col auto-cols-max gap-ds-space-100 overflow-x-auto px-ds-space-100 pb-ds-space-050">
                        {stagedAttachments.map((attachment) => (
                            <NoteAttachmentTile
                                key={attachment.assetId}
                                attachment={attachment}
                                onRemove={() => {
                                    const next = stagedAttachments.filter(
                                        (item) => item.assetId !== attachment.assetId,
                                    );
                                    onStagedAttachmentsChange(next);
                                    if (next.length < NOTES_MAX_ATTACHMENTS) {
                                        setUploadError(null);
                                    }
                                }}
                            />
                        ))}
                    </div>
                ) : null}
                {uploadError !== null ? (
                    <div className="grid px-ds-space-150">
                        <div
                            className="grid grid-cols-[max-content_minmax(0,1fr)] items-start gap-ds-space-100 rounded-ds-border-radius-100 p-ds-space-150"
                            style={{backgroundColor: token("color.background.danger")}}
                        >
                            <ErrorIcon label="" color={token("color.icon.danger")} />
                            <div className="grid gap-ds-space-025">
                                <AnankeText size="small" color="color.text.danger">
                                    <span className="font-ds-font-weight-medium">Upload error</span>
                                </AnankeText>
                                <AnankeText size="small" color="color.text.danger">
                                    {uploadError}
                                </AnankeText>
                            </div>
                        </div>
                    </div>
                ) : null}
                {isOverLimit === true ? (
                    <div className="grid px-ds-space-150">
                        <div
                            className="grid p-ds-space-100 rounded-ds-border-radius-050"
                            style={{
                                background: token("color.background.danger"),
                                color: token("color.text.danger"),
                            }}
                        >
                            <AnankeText size="small" color="color.text.danger">
                                Character limit reached. You have reached the maximum character limit for notes.
                            </AnankeText>
                        </div>
                    </div>
                ) : null}
                {stagedAttachments.length > 0 ? (
                    <div className="grid grid-flow-col auto-cols-max items-center gap-ds-space-150 px-ds-space-150 pb-ds-space-050">
                        <AnankeButton
                            type="button"
                            appearance="subtle"
                            spacing="compact"
                            isDisabled={isAtAttachmentLimit === true || isUploading === true}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            + Add more files
                        </AnankeButton>
                        <AnankeButton
                            type="button"
                            appearance="subtle"
                            spacing="compact"
                            onClick={() => {
                                onStagedAttachmentsChange([]);
                                setUploadError(null);
                            }}
                        >
                            <span style={{color: token("color.text.danger")}}>✕ Clear all</span>
                        </AnankeButton>
                    </div>
                ) : null}
            </div>
            {showFooter === true ? footer : null}
        </div>
    );
}

export function createEmptyComposerId(): Uuid {
    return generateUuid();
}
